/**
 * Agent Packages management API service
 *
 * Provides methods for managing Nebula Agent packages.
 * Includes upload, versioning, activation, and download functionality.
 */

import { apiClient } from './api'

// ===== Types =====

export type AgentPackage = {
  id: string // UUID
  version: string
  filename: string
  storage_type: string
  size_bytes: number
  size_human: string
  sha256_hash: string
  is_active: boolean
  is_latest: boolean
  release_notes: string | null
  min_proxmox_version: string | null
  download_url: string
  created_at: string
  uploaded_by_id: string // UUID
  uploaded_by_name: string
}

export type AgentPackageListResponse = {
  packages: AgentPackage[]
  total: number
  active_version: string | null
}

export type AgentPackageUpload = {
  version: string
  release_notes?: string
  min_proxmox_version?: string
  set_as_active?: boolean
}

export type AgentPackageUpdate = {
  release_notes?: string
  min_proxmox_version?: string
  is_latest?: boolean
}

export type AgentPackageActivate = {
  set_as_latest?: boolean
}

// ===== API Functions =====

const agentPackagesApi = {
  /**
   * Upload a new agent package
   */
  uploadPackage: async (file: File, data: AgentPackageUpload): Promise<AgentPackage> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('version', data.version)
    if (data.release_notes) {
      formData.append('release_notes', data.release_notes)
    }
    if (data.min_proxmox_version) {
      formData.append('min_proxmox_version', data.min_proxmox_version)
    }
    formData.append('set_as_active', String(data.set_as_active ?? true))

    return apiClient.post('/api/v1/agent-packages/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  /**
   * List all agent packages
   */
  listPackages: (): Promise<AgentPackageListResponse> => {
    return apiClient.get('/api/v1/agent-packages')
  },

  /**
   * Get the currently active package (public endpoint)
   */
  getActivePackage: (): Promise<AgentPackage> => {
    return apiClient.get('/api/v1/agent-packages/active')
  },

  /**
   * Get a specific package by ID
   */
  getPackage: (id: string): Promise<AgentPackage> => {
    return apiClient.get(`/api/v1/agent-packages/${id}`)
  },

  /**
   * Activate a package for downloads
   */
  activatePackage: (id: string, data?: AgentPackageActivate): Promise<AgentPackage> => {
    return apiClient.post(`/api/v1/agent-packages/${id}/activate`, data || {})
  },

  /**
   * Update package metadata
   */
  updatePackage: (id: string, data: AgentPackageUpdate): Promise<AgentPackage> => {
    return apiClient.patch(`/api/v1/agent-packages/${id}`, data)
  },

  /**
   * Delete a package
   */
  deletePackage: (id: string): Promise<void> => {
    return apiClient.delete(`/api/v1/agent-packages/${id}`)
  },

  /**
   * Get download URL for a package
   */
  getDownloadUrl: (id: string): string => {
    return `/api/v1/agent-packages/${id}/download`
  },
}

// ===== Helper Functions =====

/**
 * Format file size to human-readable string
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}

/**
 * Validate semver version format
 */
export const isValidVersion = (version: string): boolean => {
  const semverRegex = /^\d+\.\d+\.\d+(-[a-z0-9]+)?$/i
  return semverRegex.test(version)
}

/**
 * Compare two semver versions
 * Returns: -1 if a < b, 0 if a == b, 1 if a > b
 */
export const compareVersions = (a: string, b: string): number => {
  const partsA = a.split('-')[0].split('.').map(Number)
  const partsB = b.split('-')[0].split('.').map(Number)

  for (let i = 0; i < 3; i++) {
    if (partsA[i] > partsB[i]) return 1
    if (partsA[i] < partsB[i]) return -1
  }

  // Handle pre-release tags (versions without tags are greater)
  const tagA = a.split('-')[1]
  const tagB = b.split('-')[1]
  if (!tagA && tagB) return 1
  if (tagA && !tagB) return -1
  if (tagA && tagB) return tagA.localeCompare(tagB)

  return 0
}

/**
 * Sort packages by version (newest first)
 */
export const sortPackagesByVersion = (packages: AgentPackage[]): AgentPackage[] => {
  return [...packages].sort((a, b) => compareVersions(b.version, a.version))
}

/**
 * Get status badge color based on package status
 */
export const getPackageStatusColor = (pkg: AgentPackage): { bg: string; text: string } => {
  if (pkg.is_active) {
    return { bg: 'bg-green-100', text: 'text-green-700' }
  }
  if (pkg.is_latest) {
    return { bg: 'bg-blue-100', text: 'text-blue-700' }
  }
  return { bg: 'bg-gray-100', text: 'text-gray-700' }
}

/**
 * Get status label for a package
 */
export const getPackageStatusLabel = (pkg: AgentPackage): string => {
  if (pkg.is_active) return 'Ativo'
  if (pkg.is_latest) return 'Mais recente'
  return 'Disponível'
}

/**
 * Copy text to clipboard
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

export default agentPackagesApi
